import React, { Suspense, lazy, useLayoutEffect } from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import App from "./App";
import { ContentProvider } from "./cms/ContentContext";

const CmsPage = lazy(() => import("./pages/CmsPage"));
const BrandLinkPage = lazy(() => import("./pages/BrandLinkPage"));
const QrDemoPage = lazy(() => import("./pages/QrDemoPage"));
const QrRedirectHandler = lazy(() =>
  import("./pages/QrDemoPage").then((m) => ({ default: m.QrRedirectHandler }))
);
const NotFoundPage = lazy(() =>
  import("./pages/CmsPage").then((m) => ({ default: m.NotFoundPage }))
);

/**
 * Removes the HTML boot overlay on the first paint of `.app`.
 * Fade is visual only — no minimum display time, no wait on CMS/fonts.
 */
function DismissSiteLoader({ children }) {
  useLayoutEffect(() => {
    const el = document.getElementById("site-loader");
    const rootEl = document.getElementById("root");
    if (!el || !rootEl) return undefined;

    let failsafe;
    let dismissed = false;

    const remove = () => {
      if (el.parentNode) el.remove();
    };

    const dismiss = () => {
      if (dismissed) return;
      dismissed = true;
      el.setAttribute("aria-busy", "false");
      const reduced =
        typeof window.matchMedia === "function" &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      if (reduced) {
        remove();
        return;
      }
      el.classList.add("site-loader--gone");
      el.addEventListener("transitionend", remove, { once: true });
      failsafe = window.setTimeout(remove, 400);
    };

    if (rootEl.firstElementChild || rootEl.querySelector(".app")) {
      dismiss();
      return () => window.clearTimeout(failsafe);
    }

    const observer = new MutationObserver(() => {
      if (rootEl.firstElementChild || rootEl.querySelector(".app")) {
        observer.disconnect();
        dismiss();
      }
    });
    observer.observe(rootEl, { childList: true, subtree: true });

    return () => {
      observer.disconnect();
      window.clearTimeout(failsafe);
    };
  }, []);

  return children;
}

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(
  <React.StrictMode>
    <DismissSiteLoader>
      <BrowserRouter>
        <ContentProvider>
          <Suspense fallback={null}>
            <Routes>
              <Route path="/" element={<App />} />
              <Route path="/qr-demo" element={<QrDemoPage />} />
              <Route path="/qr-landing" element={<QrDemoPage />} />
              <Route path="/qr/:code" element={<QrRedirectHandler />} />
              <Route path="/r/:code" element={<QrRedirectHandler />} />
              <Route path="/page/ifp-petro" element={<BrandLinkPage overrideSlug="ifp-petro" />} />
              <Route path="/page/dcq" element={<BrandLinkPage overrideSlug="dcq" />} />
              <Route path="/page/eosa" element={<BrandLinkPage overrideSlug="eosa" />} />
              <Route path="/page/enablyst" element={<BrandLinkPage overrideSlug="enablyst" />} />
              <Route path="/ifp-petro" element={<BrandLinkPage overrideSlug="ifp-petro" />} />
              <Route path="/dcq" element={<BrandLinkPage overrideSlug="dcq" />} />
              <Route path="/eosa" element={<BrandLinkPage overrideSlug="eosa" />} />
              <Route path="/enablyst" element={<BrandLinkPage overrideSlug="enablyst" />} />
              <Route path="/brand/:slug" element={<BrandLinkPage />} />
              <Route path="/page/:slug" element={<CmsPage />} />
              <Route path="/404" element={<NotFoundPage />} />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Suspense>
        </ContentProvider>
      </BrowserRouter>
    </DismissSiteLoader>
  </React.StrictMode>
);
